import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import DualList from "../../../../components/DualList/DualList";
import {
  FormControlLabel,
  Checkbox,
  Divider,
  Grid,
  Typography,
  Box,
  Button
} from "@mui/material";
import { Description as PageIcon } from "@mui/icons-material";
import {
  fetchCourseEnrollments,
  fetchCourses,
  updateCourseEnrollments
} from "../../../../redux/actions/contentCoursesActions";
import {
  intersectionBy,
  intersectionWith,
  xorBy,
  differenceBy,
  sortBy,
  unionBy
} from "lodash";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { findByKey } from "../../../../utils/helpers";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  PositionsFilter,
  LocationsFilter,
  GroupsFilter,
  DatePickerField
} from "../../../../components/FormikFields";
import ButtonGroup from "../../../../components/Buttons/ButtonGroup";
import { isBeforeDate, isValidDate } from "../../../../utils/datetime";
import { TextField } from "formik-mui";
import { Search } from "@mui/icons-material";
import {
  assignmentTypeIds,
  contentHosts,
  trainingObjectStatusIds,
  resourceObjectTypes
} from "../../../../constants";
import { visionURL } from "../../../../config/environment";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { useUsers } from "../../../../hooks/apiHooks/users";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar
} from "../../../../redux/actions/snackbarActions";

const employeesReportUrl = `${visionURL}/reports/8849/run_report?filter[course_profile]=`;

const getValidationSchema = t => {
  return Yup.object().shape({
    hiredSince: Yup.mixed().test(
      "hiredSince",
      t("validationMessages.validDate"),
      function() {
        return (
          !this.parent.hiredSince ||
          (isValidDate(this.parent.hiredSince) &&
            !isBeforeDate(this.parent.hiredSince, "01/01/1900"))
        );
      }
    )
  });
};

/**
 * Returns an object containing the properties that will be passed into
 * the DualListItem component. Hide arrow should be true if the employee
 * is already enrolled or for Brsp courses
 */
const getListItemDetails = (item, isAssigned, hideArrow) => {
  return {
    id: item.id,
    name: item.fullName,
    firstName: item.firstName,
    lastName: item.lastName,
    subtitles: [
      item.position ? item.position.name : "",
      item.location ? item.location.name : ""
    ],
    highlighted: isAssigned,
    hideArrow
  };
};

/**
 * Filters all non enrolled employees by the applied filters and removes
 * employees that are not currently enrolled but are in the "Enrolled Employees"
 * column
 */
const filterEmployees = (
  { position, location, group, fullName, hiredSince },
  nonEnrolledEmployees,
  employeesToEnroll,
  sessionData
) => {
  let filteredEmployees = differenceBy(
    nonEnrolledEmployees,
    employeesToEnroll,
    "id"
  );
  filteredEmployees = filteredEmployees.filter(employee => {
    let includeEmployee = true;
    if (position && employee.position.id !== position) {
      includeEmployee = false;
    }
    if (location && employee.location.id !== location) {
      includeEmployee = false;
    }
    //exclude the instructor
    if (employee.id == sessionData.iltInstructor.userId) {
      includeEmployee = false;
    }
    if (
      group &&
      !employee.groups.find(employeeGroup => employeeGroup.id === group.id)
    ) {
      includeEmployee = false;
    }
    if (
      fullName &&
      employee.fullName.toLowerCase().indexOf(fullName.toLowerCase()) === -1
    ) {
      includeEmployee = false;
    }
    if (hiredSince && isBeforeDate(employee.hireDate, hiredSince)) {
      includeEmployee = false;
    }
    return includeEmployee;
  });
  return filteredEmployees;
};

/**
 * Returns true if the user is assigned to the course
 */
const getIsAssigned = (userAssignments, employeeId) =>
  userAssignments
    ? !!userAssignments.find(({ userId }) => `${userId}` === employeeId)
    : false;

const EnrollEmployeesFilters = ({ onFilterEmployees, onClearAllFilters }) => {
  const { t } = useTranslation();
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} lg={4}>
        <Field
          fullWidth
          label={t("filterLabels.employeeName")}
          variant="filled"
          component={TextField}
          name="fullName"
          InputProps={{
            endAdornment: <Search color="action" />,
            disableUnderline: true
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <PositionsFilter />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <LocationsFilter />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <GroupsFilter
          filters={{ active: true, hasEmployees: true }}
          getValue={group => group}
          fields={{ group: ["name"] }}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <Field
          name="hiredSince"
          component={DatePickerField}
          label={t("filterLabels.hiredSince")}
        />
      </Grid>
      <Grid container item xs={12} sm={6} lg={4} sx={{ alignItems: "center" }}>
        <ButtonGroup align="right" flex={1}>
          <Button color="primary" onClick={() => onClearAllFilters()}>
            {t("courseSession.clearAllBtn")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="wide"
            onClick={() => onFilterEmployees()}
          >
            {t("courseSession.filterListBtn")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};

const CourseSessionEnrollEmployees = ({ sessionData, courseData }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [employeesToEnroll, setEmployeesToEnroll] = useState([]);
  const [newNonEnrolledEmployees, setNewNonEnrolledEmployees] = useState([]);
  const { getUsers } = useUsers({});
  const [filters, setFilters] = useState({});
  const [employees, setEmployees] = useState({});

  const { enrollments, iltInstructor } = sessionData;
  const { userAssignments } = courseData;

  const { loading, isUpdating } = useSelector(
    ({
      contentCoursesState: { isFetchingEnrollments, isUpdatingEnrollments }
    }) => {
      return {
        loading: isFetchingEnrollments || isFetchingEnrollments,
        isUpdating: isUpdatingEnrollments
      };
    }
  );

  useEffect(() => {
    const fields = {
      user: [
        "firstName",
        "lastName",
        "fullName",
        "hireDate",
        "name",
        "position",
        "location",
        "groups"
      ],
      position: ["name"],
      location: ["name"]
    };
    const include = ["position", "location"];
    const loadEmployees = async () => {
      setEmployees(
        (
          await getUsers({
            fields,
            include,
            filters: {
              active: "{}"
            },
            sort: ["fullName"],
            custom: {
              usersToInstruct: true
            }
          })
        ).data
      );
    };
    loadEmployees();
  }, []);

  useEffect(() => {
    if (sessionData) {
      dispatch(fetchCourseEnrollments(`${sessionData.id}`));
    }
    dispatch(fetchCourses(sessionData.id, { include: "userAssignments" }));
  }, []);

  useEffect(() => {
    filterAndUpdateLists();
  }, [employees, filters]);

  const enrolledEmployees = useMemo(
    () => intersectionBy(employees, enrollments, "id"),
    [employees, enrollments]
  );

  const nonEnrolledEmployees = useMemo(
    () => xorBy(employees, enrolledEmployees, "id"),
    [employees, enrolledEmployees]
  );

  // Remove self assigned employees because we don't want to highlight those
  // Remove excluded assignments
  const employeeAssignments = userAssignments
    ? userAssignments.filter(
        ({ assignmentTypeId, excluded }) =>
          assignmentTypeId !== assignmentTypeIds.selfAssigned && !excluded
      )
    : [];

  const availableSeats =
    sessionData.iltSeats - enrolledEmployees.length - employeesToEnroll.length;

  const isBrspCourse = sessionData.contentHost === contentHosts.brsp;

  const notAnActiveSession =
    sessionData.statusTypeId !== trainingObjectStatusIds.active;

  const disableSaveButton =
    availableSeats < 0 ||
    notAnActiveSession ||
    isBrspCourse ||
    loading ||
    isUpdating;

  const filterAndUpdateLists = () => {
    setNewNonEnrolledEmployees(
      filterEmployees(
        filters,
        nonEnrolledEmployees,
        employeesToEnroll,
        sessionData
      )
    );
  };

  const handleRemoveAll = () => {
    const newEnrolledList = [];
    newEnrolledList.push(...newNonEnrolledEmployees, ...employeesToEnroll);
    setNewNonEnrolledEmployees(newEnrolledList);
    setEmployeesToEnroll([]);
  };

  const handleAddAll = () => {
    const newEnrolledList = [];
    newEnrolledList.push(...employeesToEnroll, ...newNonEnrolledEmployees);
    setEmployeesToEnroll(newEnrolledList);
    setNewNonEnrolledEmployees([]);
  };

  const handleAddToEnrolledList = ({ id }) => {
    const newEnrolledList = [
      ...employeesToEnroll,
      findByKey(nonEnrolledEmployees, "id", id)
    ];
    setEmployeesToEnroll(newEnrolledList);
    const newNonEnrolledList = newNonEnrolledEmployees.filter(
      employee => employee.id !== id
    );
    setNewNonEnrolledEmployees(newNonEnrolledList);
  };

  const handleAddToNonEnrolledList = ({ id }) => {
    const newNonEnrolledList = [
      ...newNonEnrolledEmployees,
      findByKey(employeesToEnroll, "id", id)
    ];
    setNewNonEnrolledEmployees(newNonEnrolledList);
    const newEnrolledList = employeesToEnroll.filter(
      employee => employee.id !== id
    );
    setEmployeesToEnroll(newEnrolledList);
  };

  const handleDisplayAssignedOnlyChange = displayAssignedOnly => {
    if (displayAssignedOnly) {
      setNewNonEnrolledEmployees(
        intersectionWith(
          newNonEnrolledEmployees,
          employeeAssignments,
          (employee, assignment) => employee.id === `${assignment.userId}`
        )
      );
    } else {
      filterAndUpdateLists();
    }
  };

  const handleSaveEnrollments = () => {
    const enrollments = employeesToEnroll.map(employee => ({
      id: employee.id,
      type: resourceObjectTypes.enrollments,
      enroll: true
    }));
    dispatch(updateCourseEnrollments(`${sessionData.id}`, enrollments)).then(
      response => {
        if (response?.data) {
          dispatch(
            enqueueSuccessSnackbar({
              message: t("courseSession.enrollSuccessMessage")
            })
          );
        } else {
          dispatch(
            enqueueErrorSnackbar({
              message: t("courseSession.enrollFailureMessage")
            })
          );
        }
        setEmployeesToEnroll([]);
      }
    );
  };

  const handleViewReport = () => {
    const reportUrl = `${employeesReportUrl}${
      sessionData.id
    }|${encodeURIComponent(sessionData.name).replace(/%20/g, "+")}`;
    window.open(reportUrl, "_blank");
  };

  const enrolledEmployeesListItems = enrolledEmployees.map(employee =>
    getListItemDetails(
      employee,
      getIsAssigned(employeeAssignments, employee.id),
      true
    )
  );

  const newEnrolledEmployeesListItems = employeesToEnroll.map(employee =>
    getListItemDetails(
      employee,
      getIsAssigned(employeeAssignments, employee.id),
      isBrspCourse || employee.id == iltInstructor.userId
    )
  );

  const nonEnrolledEmployeesListItems = newNonEnrolledEmployees.map(employee =>
    getListItemDetails(
      employee,
      getIsAssigned(employeeAssignments, employee.id),
      isBrspCourse || employee.id == iltInstructor.userId
    )
  );

  return (
    <Formik
      initialValues={{
        location: "",
        position: "",
        group: "",
        fullName: "",
        hiredSince: null
      }}
      validationSchema={getValidationSchema(t)}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        setFilters({ ...values });
        setSubmitting(false);
      }}
    >
      {({ submitForm, resetForm }) => (
        <Form>
          <Box display="flex" flexDirection="column" mt={3}>
            <EnrollEmployeesFilters
              onFilterEmployees={() => submitForm()}
              onClearAllFilters={() => {
                setFilters({});
                resetForm();
              }}
            />
            <Divider sx={{ my: 3 }} />
            {loading ? (
              <Box pt={5} pb={15}>
                <LoadingSpinner />
              </Box>
            ) : (
              <DualList
                renderList={(items, DualListItem) => (
                  <Box flex={1} minHeight={460} mt={3}>
                    <AutoSizer>
                      {({ height, width }) => (
                        <List
                          className="List"
                          height={height}
                          itemCount={items.length}
                          itemSize={89}
                          width={width}
                        >
                          {({ index, style }) => {
                            return (
                              <div style={style}>
                                {DualListItem(items[index])}
                              </div>
                            );
                          }}
                        </List>
                      )}
                    </AutoSizer>
                  </Box>
                )}
                hideAddAllButton={isBrspCourse}
                hideRemoveAllButton={isBrspCourse}
                disabled={isUpdating}
                onAddAll={handleAddAll}
                removeAllButtonText={t(
                  "courseSession.removeAllNotYetEnrolledBtn"
                )}
                onRemoveAll={handleRemoveAll}
                onAddToSelectedList={handleAddToEnrolledList}
                onAddToUnselectedList={handleAddToNonEnrolledList}
                selectedListHeader={`${t(
                  "courseSession.enrolledEmployeesListHeader"
                )}`}
                unselectedListHeader={`${t(
                  "courseSession.nonEnrolledEmployeesListHeader"
                )}`}
                selectedList={sortBy(
                  unionBy(
                    enrolledEmployeesListItems,
                    newEnrolledEmployeesListItems,
                    "id"
                  ),
                  employee => employee.lastName.toLowerCase()
                )}
                unselectedList={sortBy(
                  nonEnrolledEmployeesListItems,
                  employee => employee.lastName.toLowerCase()
                )}
                selectedListAction={
                  <span>
                    <Typography variant="caption">
                      {t("courseSession.availableSeatsCaption")}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={availableSeats < 0 ? "error" : "textPrimary"}
                    >
                      {` ${availableSeats}`}
                    </Typography>
                  </span>
                }
                unselectedListAction={
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={({ target: { checked } }) =>
                          handleDisplayAssignedOnlyChange(checked)
                        }
                        value="assignedOnly"
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {t("courseSession.displayAssignedEmployeesOnly")}
                      </Typography>
                    }
                  />
                }
              />
            )}
            {availableSeats < 0 && (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ flexBasis: { xs: "unset", sm: "50%" }, pl: 3 }}
                >
                  {t("validationMessages.cannotExceedAvailableSeats")}
                </Typography>
              </Box>
            )}
            <Divider sx={{ my: 3 }} />
            <Box mt={2} display="flex" flex={1} justifyContent="space-between">
              <Button
                color="primary"
                startIcon={<PageIcon />}
                onClick={() => handleViewReport()}
              >
                {t("courseSession.enrollmentQuickReportBtn")}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={() => handleSaveEnrollments()}
                disabled={disableSaveButton}
              >
                {t("courseSession.saveEnrollmentBtn")}
              </Button>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

CourseSessionEnrollEmployees.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    })
  })
};

export default CourseSessionEnrollEmployees;
